<template>
  <div class="org-course-detail">
    <div class="page-header">
      <el-button icon="el-icon-arrow-left" @click="$router.push('/org/courses')">返回</el-button>
      <div class="title">{{ course.course_name || '课程详情' }}</div>
    </div>

    <el-card>
      <el-tabs v-model="activeTab">
        <el-tab-pane label="课程信息" name="info">
          <el-form label-width="90px" class="info-form">
            <el-form-item label="课程名称">
              <el-input v-model="courseForm.courseName" />
            </el-form-item>
            <el-form-item label="学期">
              <el-input v-model="courseForm.term" />
            </el-form-item>
            <el-form-item label="封面">
              <el-input v-model="courseForm.image" placeholder="可选：封面URL" />
            </el-form-item>
            <el-form-item label="简介">
              <el-input v-model="courseForm.profile" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item label="目标">
              <el-input v-model="courseForm.target" type="textarea" :rows="3" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="saveCourse" :loading="savingCourse">保存课程信息</el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <el-tab-pane label="班级管理" name="classes">
          <div class="actions">
            <el-button type="primary" icon="el-icon-circle-plus-outline" @click="openCreateClassDialog">创建班级</el-button>
          </div>

          <el-table :data="classes" v-loading="loadingClasses" style="width: 100%">
            <el-table-column prop="name" label="班级名称" min-width="180" />
            <el-table-column prop="inviteCode" label="邀请码" width="140">
              <template slot-scope="scope">
                <span style="font-family: monospace;">{{ scope.row.inviteCode }}</span>
                <el-button type="text" icon="el-icon-document-copy" @click="copyText(scope.row.inviteCode)">复制</el-button>
              </template>
            </el-table-column>
            <el-table-column prop="description" label="描述" min-width="220" />
            <el-table-column label="操作" width="220">
              <template slot-scope="scope">
                <el-button type="text" @click="openEditClassDialog(scope.row)">编辑</el-button>
                <el-button type="text" @click="openAddStudentDialog(scope.row)">拉人</el-button>
                <el-button type="text" @click="openMembersDialog(scope.row)">成员</el-button>
                <el-button type="text" @click="deleteOneClass(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="!loadingClasses && (!classes || classes.length === 0)" class="empty">
            <div class="empty-title">暂无班级</div>
            <div class="empty-desc">创建一个班级后会自动生成邀请码</div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="课程资料" name="resources">
          <div class="section-header">
            <div class="section-title">课程资料</div>
            <div class="section-actions">
              <el-input v-model="uploadTitle" placeholder="资料标题(可选)" size="small" style="width:220px;" />
              <el-upload
                :show-file-list="false"
                action="/api/amc/material/upload"
                :data="{ courseId: courseId, title: uploadTitle }"
                :headers="uploadHeaders"
                :on-success="onUploadSuccess"
                :on-error="onUploadError"
              >
                <el-button type="primary" size="small">上传</el-button>
              </el-upload>
              <el-button size="small" @click="fetchMaterials" :loading="materialsLoading">刷新</el-button>
            </div>
          </div>

          <div v-if="materialsLoading" class="loading-container" v-loading="true" element-loading-text="正在加载资料..." style="height:180px;" />
          <template v-else>
            <el-table v-if="materialList && materialList.length" :data="materialList" size="small" border>
              <el-table-column type="index" width="60" label="#" />
              <el-table-column prop="title" label="标题" min-width="200">
                <template slot-scope="{ row }">{{ row.title || row.fileName }}</template>
              </el-table-column>
              <el-table-column prop="fileName" label="文件名" min-width="220" show-overflow-tooltip />
              <el-table-column prop="fileSize" label="大小" width="120">
                <template slot-scope="{ row }">{{ formatSize(row.fileSize) }}</template>
              </el-table-column>
              <el-table-column prop="mimeType" label="类型" width="160" show-overflow-tooltip />
              <el-table-column prop="createdAt" label="上传时间" width="200">
                <template slot-scope="{ row }">{{ formatDateTime(row.createdAt) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="220">
                <template slot-scope="{ row }">
                  <a :href="withApiPrefix(row.url)" target="_blank" rel="noopener noreferrer">
                    <el-button type="text" size="mini">查看</el-button>
                  </a>
                  <el-button type="text" size="mini" @click="downloadMaterial(row)">下载</el-button>
                  <el-popconfirm title="确定删除该资料吗？" @onConfirm="removeMaterial(row)">
                    <el-button type="text" size="mini" slot="reference">删除</el-button>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
            <div v-else class="empty-tip">
              <i class="el-icon-folder"></i>
              <p>暂无课程资料</p>
            </div>
          </template>
        </el-tab-pane>

        <el-tab-pane label="学情分析" name="analysis">
          <div class="section-header">
            <div>
              <div class="section-title">学情分析</div>
              <div class="section-subtitle">班级整体学习情况统计</div>
            </div>
            <div class="section-actions">
              <el-button size="small" @click="loadLearningAnalysis" :loading="analysisLoading" icon="el-icon-refresh" type="primary">
                {{ analysisLoading ? '加载中...' : '刷新数据' }}
              </el-button>
            </div>
          </div>

          <el-row :gutter="24" style="margin-top: 12px; margin-bottom: 8px;" v-loading="analysisLoading" element-loading-text="正在统计数据..." element-loading-spinner="el-icon-loading">
            <el-col :xs="12" :sm="12" :md="6" :lg="6" style="margin-bottom: 16px;">
              <el-card class="analysis-card">
                <div class="card-content">
                  <div class="card-label">学生总数</div>
                  <div class="card-value">{{ analysisData.totalStudents || 0 }}</div>
                </div>
              </el-card>
            </el-col>
            <el-col :xs="12" :sm="12" :md="6" :lg="6" style="margin-bottom: 16px;">
              <el-card class="analysis-card">
                <div class="card-content">
                  <div class="card-label">作业完成率</div>
                  <div class="card-value">{{ analysisData.homeworkCompletionRate || 0 }}%</div>
                </div>
              </el-card>
            </el-col>
            <el-col :xs="12" :sm="12" :md="6" :lg="6" style="margin-bottom: 16px;">
              <el-card class="analysis-card">
                <div class="card-content">
                  <div class="card-label">讨论参与率</div>
                  <div class="card-value">{{ analysisData.discussionParticipationRate || 0 }}%</div>
                </div>
              </el-card>
            </el-col>
          </el-row>

          <el-card style="margin-top: 12px;" v-loading="analysisLoading">
            <div slot="header" class="clearfix" style="padding: 8px 0;">
              <span style="font-weight: 600; font-size: 18px; color: #303133;">学生学情列表</span>
              <el-input
                v-model="analysisSearchKeyword"
                placeholder="搜索学生姓名或学号"
                size="small"
                prefix-icon="el-icon-search"
                style="width: 260px; float: right;"
                @input="filterStudentAnalysis"
                clearable
              />
            </div>

            <div v-if="filteredStudentAnalysisList.length === 0 && !analysisLoading" class="empty-tip" style="padding: 40px 20px; text-align: center;">
              <i class="el-icon-user" style="font-size: 48px; color: #DCDFE6; margin-bottom: 12px;"></i>
              <p style="color: #909399; font-size: 14px;">{{ analysisSearchKeyword ? '未找到匹配的学生' : '暂无学生数据' }}</p>
            </div>

            <el-table
              v-else
              :data="filteredStudentAnalysisList"
              stripe
              style="width: 100%"
            >
              <el-table-column type="index" label="#" width="70" align="center" />
              <el-table-column prop="studentName" label="学生" min-width="180">
                <template slot-scope="{ row }">
                  {{ row.studentName || row.realName || row.username || row.userName || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="className" label="班级" min-width="180" show-overflow-tooltip />
              <el-table-column prop="homeworkCompletionRate" label="作业完成率" width="120" align="center">
                <template slot-scope="{ row }">{{ row.homeworkCompletionRate != null ? row.homeworkCompletionRate + '%' : '-' }}</template>
              </el-table-column>
              <el-table-column prop="discussionParticipationRate" label="讨论参与" width="120" align="center">
                <template slot-scope="{ row }">{{ row.discussionParticipationRate != null ? row.discussionParticipationRate + '%' : '-' }}</template>
              </el-table-column>
              <el-table-column prop="overallScore" label="综合表现" width="120" align="center">
                <template slot-scope="{ row }">{{ row.overallScore != null ? row.overallScore : '-' }}</template>
              </el-table-column>
            </el-table>
          </el-card>
        </el-tab-pane>

        <el-tab-pane label="数据收集" name="data-collection">
          <DataCollection v-if="courseId" :course-id="courseId" />
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-dialog
      title="创建班级"
      :visible.sync="createClassDialogVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form ref="createClassFormRef" :model="createClassForm" label-width="90px">
        <el-form-item label="班级名称" prop="name" :rules="[{ required: true, message: '班级名称不能为空', trigger: 'blur' }]">
          <el-input v-model="createClassForm.name" placeholder="例如：2026春一期" />
        </el-form-item>
        <el-form-item label="班级描述">
          <el-input v-model="createClassForm.description" placeholder="可选" />
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="createClassDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creatingClass" @click="submitCreateClass">创建</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="编辑班级"
      :visible.sync="editClassDialogVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form ref="editClassFormRef" :model="editClassForm" label-width="90px">
        <el-form-item label="班级名称" prop="name" :rules="[{ required: true, message: '班级名称不能为空', trigger: 'blur' }]">
          <el-input v-model="editClassForm.name" placeholder="班级名称" />
        </el-form-item>
        <el-form-item label="邀请码">
          <el-input v-model="editClassForm.inviteCode" placeholder="可选：留空自动生成" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="editClassForm.description" placeholder="可选" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editClassDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="savingEditClass" @click="submitEditClass">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="拉人进班"
      :visible.sync="addStudentDialogVisible"
      width="520px"
      :close-on-click-modal="false"
    >
      <div style="display:flex;gap:12px;align-items:center;flex-wrap:wrap;">
        <el-input v-model.trim="addStudentUsername" placeholder="输入用户名（可选）" style="width: 220px;" />
        <el-input v-model.number="addStudentUserId" type="number" placeholder="输入用户ID（可选）" style="width: 220px;" />
        <el-button type="primary" :loading="addingStudent" @click="submitAddStudent">确定</el-button>
      </div>
      <div style="margin-top:8px;color:#909399;font-size:12px;">优先使用用户ID；若未填ID则使用用户名</div>
    </el-dialog>

    <el-dialog
      title="班级成员"
      :visible.sync="membersDialogVisible"
      width="680px"
      :close-on-click-modal="false"
    >
      <el-table :data="members" v-loading="loadingMembers" size="small" border>
        <el-table-column type="index" width="60" label="#" />
        <el-table-column prop="id" label="ID" width="90" />
        <el-table-column prop="username" label="用户名" min-width="180" />
        <el-table-column label="操作" width="120">
          <template slot-scope="scope">
            <el-button type="text" @click="removeOneMember(scope.row)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!loadingMembers && (!members || members.length === 0)" class="empty">
        <div class="empty-title">暂无成员</div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="membersDialogVisible = false">关闭</el-button>
        <el-button :loading="loadingMembers" @click="fetchMembers">刷新</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getCourseById } from '@/api/course'
import { listClasses, createClass, updateClass, deleteClass, addStudentById, addStudentByUsername, listStudents, removeStudent } from '@/api/amcCourseClass'
import { listMaterials, deleteMaterial } from '@/api/amcCourseMaterial'
import { getCourseAnalysis } from '@/api/learningAnalysis'
import DataCollection from '@/components/course/DataCollection.vue'
import request from '@/utils/request'

export default {
  name: 'OrgCourseDetail',
  components: {
    DataCollection
  },
  data() {
    return {
      activeTab: this.$route.query.tab || 'info',
      courseId: null,
      course: {},
      courseForm: {
        courseName: '',
        term: '',
        image: '',
        profile: '',
        target: ''
      },
      savingCourse: false,

      loadingClasses: false,
      classes: [],

      createClassDialogVisible: false,
      creatingClass: false,
      createClassForm: {
        name: '',
        description: ''
      },

      editClassDialogVisible: false,
      savingEditClass: false,
      editClassTargetId: null,
      editClassForm: {
        name: '',
        inviteCode: '',
        description: ''
      },

      addStudentDialogVisible: false,
      addingStudent: false,
      addStudentTargetClassId: null,
      addStudentUsername: '',
      addStudentUserId: null,

      membersDialogVisible: false,
      loadingMembers: false,
      membersTargetClassId: null,
      members: [],

      // 课程资料
      uploadHeaders: {},
      uploadTitle: '',
      materialsLoading: false,
      materialList: [],

      // 学情分析
      analysisLoading: false,
      analysisData: {
        totalStudents: 0,
        homeworkCompletionRate: 0,
        discussionParticipationRate: 0
      },
      studentAnalysisList: [],
      filteredStudentAnalysisList: [],
      analysisSearchKeyword: ''
    }
  },
  async created() {
    this.courseId = this.$route.params.courseId
    this.applyTabFromRoute()
    await this.fetchCourse()
    await this.fetchClasses()

    try {
      const t = localStorage.getItem('token') || sessionStorage.getItem('token') || ''
      if (t) this.uploadHeaders = { Authorization: t.startsWith('Bearer ') ? t : `Bearer ${t}` }
    } catch (e) {}

    // 初次进入时根据当前 tab 自动加载
    if (this.activeTab === 'resources') {
      this.fetchMaterials()
    } else if (this.activeTab === 'analysis') {
      this.loadLearningAnalysis()
    }
  },
  watch: {
    '$route.params.courseId': async function(val) {
      this.courseId = val
      this.applyTabFromRoute()
      await this.fetchCourse()
      await this.fetchClasses()
    },
    '$route.query.tab': function() {
      this.applyTabFromRoute()
    },
    activeTab: function(val) {
      if (val === 'resources') {
        this.fetchMaterials()
      } else if (val === 'analysis') {
        this.loadLearningAnalysis()
      }
    }
  },
  methods: {
    applyTabFromRoute() {
      const tab = this.$route && this.$route.query ? this.$route.query.tab : null
      if (tab === 'classes' || tab === 'info') {
        this.activeTab = tab
      }
    },

    async fetchCourse() {
      try {
        const res = await getCourseById(this.courseId)
        if (res && res.code === 1) {
          this.course = res.data || {}
          this.courseForm = {
            courseName: this.course.course_name || '',
            term: this.course.term || '',
            image: this.course.image || '',
            profile: this.course.profile || '',
            target: this.course.target || ''
          }
        } else {
          this.$message.error((res && res.msg) || '获取课程详情失败')
        }
      } catch (e) {
        console.error('获取课程详情失败:', e)
        this.$message.error('获取课程详情失败')
      }
    },

    async saveCourse() {
      this.savingCourse = true
      try {
        // TeachCourseController 现有更新接口是 /teachcourse/update (PUT)
        const payload = {
          id: Number(this.courseId),
          course_name: this.courseForm.courseName,
          term: this.courseForm.term,
          image: this.courseForm.image,
          profile: this.courseForm.profile,
          target: this.courseForm.target
        }
        const res = await request({
          url: '/teachcourse/update',
          method: 'put',
          data: payload
        })
        if (res && res.code === 1) {
          this.$message.success('保存成功')
          await this.fetchCourse()
        } else {
          this.$message.error((res && res.msg) || '保存失败')
        }
      } catch (e) {
        console.error('保存失败:', e)
        this.$message.error('保存失败')
      } finally {
        this.savingCourse = false
      }
    },

    async fetchClasses() {
      this.loadingClasses = true
      try {
        const res = await listClasses(this.courseId)
        if (res && res.code === 1) {
          this.classes = res.data || []
        } else {
          this.$message.error((res && res.msg) || '获取班级失败')
        }
      } catch (e) {
        console.error('获取班级失败:', e)
        this.$message.error('获取班级失败')
      } finally {
        this.loadingClasses = false
      }
    },

    openCreateClassDialog() {
      this.createClassForm = { name: '', description: '' }
      this.createClassDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.createClassFormRef) this.$refs.createClassFormRef.clearValidate()
      })
    },

    async submitCreateClass() {
      if (!this.$refs.createClassFormRef) return
      this.$refs.createClassFormRef.validate(async(valid) => {
        if (!valid) return
        this.creatingClass = true
        try {
          const res = await createClass({
            courseId: Number(this.courseId),
            name: this.createClassForm.name,
            description: this.createClassForm.description
          })
          if (res && res.code === 1) {
            this.$message.success('创建成功')
            this.createClassDialogVisible = false
            await this.fetchClasses()
          } else {
            this.$message.error((res && res.msg) || '创建失败')
          }
        } catch (e) {
          console.error('创建失败:', e)
          this.$message.error('创建失败')
        } finally {
          this.creatingClass = false
        }
      })
    },

    async deleteOneClass(row) {
      try {
        await this.$confirm('确定删除该班级吗？', '提示', { type: 'warning' })
        const res = await deleteClass(row.id)
        if (res && res.code === 1) {
          this.$message.success('删除成功')
          await this.fetchClasses()
        } else {
          this.$message.error((res && res.msg) || '删除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败')
        }
      }
    },

    openEditClassDialog(row) {
      this.editClassTargetId = row && row.id
      this.editClassForm = {
        name: (row && row.name) || '',
        inviteCode: (row && row.inviteCode) || '',
        description: (row && row.description) || ''
      }
      this.editClassDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.editClassFormRef) this.$refs.editClassFormRef.clearValidate()
      })
    },

    submitEditClass() {
      if (!this.$refs.editClassFormRef) return
      this.$refs.editClassFormRef.validate(async(valid) => {
        if (!valid) return
        if (!this.editClassTargetId) return
        this.savingEditClass = true
        try {
          const payload = {
            id: Number(this.editClassTargetId),
            courseId: Number(this.courseId),
            name: this.editClassForm.name,
            inviteCode: this.editClassForm.inviteCode,
            description: this.editClassForm.description
          }
          const res = await updateClass(this.editClassTargetId, payload)
          if (res && res.code === 1) {
            this.$message.success('保存成功')
            this.editClassDialogVisible = false
            await this.fetchClasses()
          } else {
            this.$message.error((res && res.msg) || '保存失败')
          }
        } catch (e) {
          console.error('保存失败:', e)
          this.$message.error('保存失败')
        } finally {
          this.savingEditClass = false
        }
      })
    },

    openAddStudentDialog(row) {
      this.addStudentTargetClassId = row && row.id
      this.addStudentUsername = ''
      this.addStudentUserId = null
      this.addStudentDialogVisible = true
    },

    async submitAddStudent() {
      if (!this.addStudentTargetClassId) return
      if (!this.addStudentUserId && !this.addStudentUsername) {
        this.$message.warning('请输入用户ID或用户名')
        return
      }
      this.addingStudent = true
      try {
        let res
        if (this.addStudentUserId) {
          res = await addStudentById(this.addStudentTargetClassId, this.addStudentUserId)
        } else {
          res = await addStudentByUsername(this.addStudentTargetClassId, this.addStudentUsername)
        }
        if (res && res.code === 1) {
          this.$message.success('已加入')
          this.addStudentDialogVisible = false
        } else {
          this.$message.error((res && res.msg) || '操作失败')
        }
      } catch (e) {
        console.error('拉人失败:', e)
        this.$message.error('拉人失败')
      } finally {
        this.addingStudent = false
      }
    },

    openMembersDialog(row) {
      this.membersTargetClassId = row && row.id
      this.membersDialogVisible = true
      this.fetchMembers()
    },

    async fetchMembers() {
      if (!this.membersTargetClassId) return
      this.loadingMembers = true
      try {
        const res = await listStudents(this.membersTargetClassId)
        if (res && res.code === 1) {
          this.members = res.data || []
        } else {
          this.$message.error((res && res.msg) || '获取成员失败')
        }
      } catch (e) {
        console.error('获取成员失败:', e)
        this.$message.error('获取成员失败')
      } finally {
        this.loadingMembers = false
      }
    },

    async removeOneMember(row) {
      if (!this.membersTargetClassId || !row || !row.id) return
      try {
        await this.$confirm('确定移除该成员吗？', '提示', { type: 'warning' })
        const res = await removeStudent(this.membersTargetClassId, row.id)
        if (res && res.code === 1) {
          this.$message.success('已移除')
          await this.fetchMembers()
        } else {
          this.$message.error((res && res.msg) || '移除失败')
        }
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('移除失败')
        }
      }
    },

    copyText(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制')
      }).catch(() => {
        this.$message.error('复制失败，请手动复制')
      })
	  },

    // ===== 课程资料 =====
    async fetchMaterials() {
      if (!this.courseId) return
      this.materialsLoading = true
      try {
        const res = await listMaterials(this.courseId)
        const ok = !res || typeof res.code === 'undefined' || res.code === 1 || res.code === 20000
        if (!ok) {
          this.$message.error(res.msg || '获取资料失败')
          this.materialList = []
          return
        }
        const data = (res && res.data !== undefined) ? res.data : res
        this.materialList = Array.isArray(data) ? data : []
      } catch (e) {
        console.error('获取资料失败:', e)
        this.$message.error('获取资料失败')
      } finally {
        this.materialsLoading = false
      }
    },
    onUploadSuccess() {
      this.$message.success('上传成功')
      this.uploadTitle = ''
      this.fetchMaterials()
    },
    onUploadError() {
      this.$message.error('上传失败')
    },
    async removeMaterial(row) {
      if (!row || !row.id) return
      try {
        const res = await deleteMaterial(row.id)
        const ok = !res || typeof res.code === 'undefined' || res.code === 1 || res.code === 20000
        if (!ok) {
          this.$message.error(res.msg || '删除失败')
          return
        }
        this.$message.success('删除成功')
        this.fetchMaterials()
      } catch (e) {
        this.$message.error('删除失败')
      }
    },
    withApiPrefix(url) {
      if (!url) return ''
      const s = String(url)
      if (s.startsWith('http://') || s.startsWith('https://')) return s
      return s.startsWith('/api') ? s : ('/api' + (s.startsWith('/') ? '' : '/') + s)
    },
    downloadMaterial(row) {
      const url = this.withApiPrefix(row && row.url)
      if (!url) return
      window.open(url, '_blank')
    },
    formatSize(bytes) {
      const n = Number(bytes || 0)
      if (!n) return '-'
      const kb = 1024
      const mb = kb * 1024
      const gb = mb * 1024
      if (n >= gb) return (n / gb).toFixed(2) + ' GB'
      if (n >= mb) return (n / mb).toFixed(2) + ' MB'
      if (n >= kb) return (n / kb).toFixed(2) + ' KB'
      return n + ' B'
    },
    formatDateTime(val) {
      if (!val) return '-'
      try {
        const d = new Date(val)
        if (isNaN(d.getTime())) return String(val)
        const pad = (x) => String(x).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch (e) {
        return String(val)
      }
    },

    // ===== 学情分析 =====
    async loadLearningAnalysis() {
      if (!this.courseId) return
      this.analysisLoading = true
      try {
        const res = await getCourseAnalysis(this.courseId)
        const ok = !res || typeof res.code === 'undefined' || res.code === 1 || res.code === 20000
        if (!ok) {
          this.$message.error(res.msg || '加载学情失败')
          this.analysisData = { totalStudents: 0, homeworkCompletionRate: 0, discussionParticipationRate: 0 }
          this.studentAnalysisList = []
          this.filteredStudentAnalysisList = []
          return
        }

        const data = (res && res.data !== undefined) ? res.data : res
        // 兼容：后端可能返回 {summary, students} / {analysisData, studentList} / 直接数组
        const students =
          (data && Array.isArray(data.students) ? data.students : null) ||
          (data && Array.isArray(data.studentList) ? data.studentList : null) ||
          (data && Array.isArray(data.records) ? data.records : null) ||
          (Array.isArray(data) ? data : [])

        const summary = (data && (data.summary || data.analysisData || data.stats)) || {}
        const totalStudents = summary.totalStudents != null ? summary.totalStudents : students.length
        const homeworkCompletionRate = summary.homeworkCompletionRate != null ? summary.homeworkCompletionRate : 0
        const discussionParticipationRate = summary.discussionParticipationRate != null ? summary.discussionParticipationRate : 0

        this.analysisData = {
          totalStudents,
          homeworkCompletionRate,
          discussionParticipationRate
        }
        this.studentAnalysisList = students
        this.filterStudentAnalysis()
      } catch (e) {
        console.error('加载学情失败:', e)
        this.$message.error('加载学情失败')
      } finally {
        this.analysisLoading = false
      }
    },
    filterStudentAnalysis() {
      const kw = String(this.analysisSearchKeyword || '').trim().toLowerCase()
      if (!kw) {
        this.filteredStudentAnalysisList = Array.isArray(this.studentAnalysisList) ? this.studentAnalysisList : []
        return
      }
      const list = Array.isArray(this.studentAnalysisList) ? this.studentAnalysisList : []
      this.filteredStudentAnalysisList = list.filter(s => {
        const name = (s.studentName || s.realName || s.username || s.userName || '').toLowerCase()
        return name.includes(kw)
      })
    }
  }
}
</script>

<style scoped>
.org-course-detail {
  padding: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.section-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.analysis-card .card-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.analysis-card .card-label {
  font-size: 12px;
  color: #909399;
}

.analysis-card .card-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
}

.empty-tip {
  padding: 24px 0;
  text-align: center;
  color: #909399;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.empty {
  padding: 24px 0;
  text-align: center;
}

.empty-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
}

.empty-desc {
  font-size: 12px;
  color: #909399;
}

.info-form {
  max-width: 720px;
}
</style>
